import '../../styles/restScreens.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModalSurface from '../shared/ModalSurface';
import AddressField from '../shared/AddressField';
import TimeCombo from './foundation/TimeCombo';
import GuestVisibilityChecklist from './foundation/GuestVisibilityChecklist';
import VendorPicker, { HotelsField } from './foundation/VendorPicker';
import { loadEventPlaces, loadInvites, loadVendors } from './foundation/foundationApi';
import './foundation/eventsFoundation.css';
import { TRANSPORT_MODES, timeProblems } from '../../lib/events/foundation';
import { BLANK_EVENT_FORM, checklistNotes } from './eventConstants';
import { ACTION_PILL_CLASS, ACTION_PILL_QUIET_CLASS, INPUT_CLASS } from './eventStyles';
import { DEFAULT_EVENT_WORDS } from './eventWords';

// Create / edit an event. M6 rule: this form holds typed text, so Escape never closes it (closeOnEscape={false});
// the footer Cancel and the phone dialog's x still do. Plain-English labels; the checklist from a starter kit is offered as editable notes,
// never forced. An event always starts as "Planned" - the database keeps it there for now.
export default function EventFormModal({ mode = 'create', initial = BLANK_EVENT_FORM, kits = [], entities = [], canEdit = true, saving = false, error = '', onSave, onClose, words = DEFAULT_EVENT_WORDS, client = null, engagementId = null, focusSection = null }) {
    const [form, setForm] = useState({ ...BLANK_EVENT_FORM, ...initial });
    const [problem, setProblem] = useState('');
    const isEdit = mode === 'edit';
    const readOnly = !canEdit;
    const title = readOnly ? `${words.event} details` : isEdit ? `Edit ${words.eventLower}` : `New ${words.eventLower}`;
    const selectedKit = useMemo(() => kits.find((kit) => kit.key === form.event_type), [kits, form.event_type]);
    const needsEntityPick = entities.length > 1;
    // P4: on an existing event the invitation settings load from the database; the section only shows once it has them, so a save can never blank them.
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [vendorList, setVendorList] = useState({ vendors: [], ready: false, accountId: null });
    const original = useRef({ venue: null, provider: null, hotels: '[]' });
    const settingsRef = useRef(null);
    const showSettings = !readOnly && (!isEdit || settingsLoaded);
    const loadVendorList = React.useCallback(async () => {
        if (!client) return;
        try {
            const r = isEdit && engagementId ? await loadEventPlaces(client, engagementId) : await loadVendors(client, null, false);
            setVendorList({ vendors: Array.isArray(r?.vendors) ? r.vendors : [], ready: Array.isArray(r?.vendors), accountId: r?.account_id || null });
            return r;
        } catch { setVendorList((old) => ({ ...old, ready: false })); return null; }
    }, [client, isEdit, engagementId]);
    useEffect(() => {
        if (readOnly || !client) return undefined;
        let live = true;
        loadVendorList().then((r) => {
            if (!live || !isEdit || !r?.places) return;
            const hotels = r.places.hotels || [];
            original.current = { venue: r.places.venue?.vendor_id || null, provider: r.places.transport_provider?.vendor_id || null, hotels: JSON.stringify(hotels) };
            setForm((previous) => ({ ...previous, venue_vendor_id: r.places.venue?.vendor_id || null, provider_vendor_id: r.places.transport_provider?.vendor_id || null, hotels }));
        });
        if (isEdit && engagementId) {
            loadInvites(client, engagementId).then((r) => {
                if (!live || !r?.options) return;
                const o = r.options;
                setForm((previous) => ({ ...previous, plus_ones_enabled: Boolean(o.plus_ones_enabled), plus_ones_max: o.plus_ones_max || 1, guest_transport_mode: o.guest_transport_mode || 'client_arranged', transport_provider_name: o.transport_provider_name || '',
                    coordinator_name: o.coordinator_name || '', coordinator_phone: o.coordinator_phone || '', coordinator_email: o.coordinator_email || '', guest_visibility: o.guest_visibility || {} }));
                setSettingsLoaded(true);
            }).catch(() => {});
        }
        return () => { live = false; };
    }, [client, isEdit, engagementId, readOnly, loadVendorList]);
    useEffect(() => {
        if (focusSection === 'guest-settings' && showSettings && settingsRef.current) {
            settingsRef.current.scrollIntoView?.({ block: 'start' });
            settingsRef.current.focus?.();
        }
    }, [focusSection, showSettings]);
    const picker = { client, accountId: vendorList.accountId, vendors: vendorList.vendors, ready: vendorList.ready, refresh: loadVendorList };

    const set = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

    const chooseType = (event) => {
        const key = event.target.value;
        setForm((previous) => {
            const kit = kits.find((item) => item.key === key);
            // Offer the kit's checklist only when the notes are still empty, so typed notes are never overwritten.
            const notes = !previous.notes.trim() && !isEdit && kit ? checklistNotes(kit) : previous.notes;
            return { ...previous, event_type: key, notes };
        });
    };

    const submit = (event) => {
        event.preventDefault();
        if (readOnly) return;
        if (!form.event_name.trim()) return setProblem(`Give the ${words.eventLower} a name.`);
        if (!form.event_date) return setProblem(`Pick the ${words.eventLower} date.`);
        if (form.event_type === 'other' && !String(form.event_type_other || '').trim()) return setProblem(`Type a short name for this kind of ${words.eventLower}.`);
        // New events must have both times. An older event with no times may stay that way until someone fills one in.
        if (!isEdit || form.start_time || form.end_time) {
            const [timeProblem] = timeProblems({ start_date: form.event_date, end_date: form.end_date, start_time: form.start_time, end_time: form.end_time });
            if (timeProblem) return setProblem(timeProblem);
        }
        if (showSettings && form.plus_ones_enabled && !(Number(form.plus_ones_max) >= 1 && Number(form.plus_ones_max) <= 10)) return setProblem('Enter how many plus-ones each guest may bring, 1 to 10.');
        if (!form.entity_id && !isEdit) return setProblem(`Choose which of your businesses this ${words.eventLower} belongs to.`);
        setProblem('');
        const hotels = (form.hotels || []).filter((h) => String(h.name || '').trim());
        // Only a pick, or a change to the hotels, needs the places call: a venue or provider typed by hand is already on the event's own columns.
        const places_changed = isEdit
            ? JSON.stringify(form.hotels || []) !== original.current.hotels || (form.venue_vendor_id || null) !== original.current.venue || (form.provider_vendor_id || null) !== original.current.provider
            : Boolean(form.venue_vendor_id || form.provider_vendor_id || hotels.length);
        onSave({ ...form, guest_settings_loaded: isEdit && settingsLoaded, places_changed });
    };

    const label = 'block text-sm font-medium text-[var(--ink)] mb-1';
    const shownError = problem || error;

    return (
        <ModalSurface
            label={title}
            onClose={onClose}
            closeOnEscape={false}
            panelProps={{ 'data-m6-layout': 'modals', 'data-m6-component': 'EventFormModal' }}
            closeDisabled={saving}
            busy={saving}
            overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            panelClassName="bg-[var(--surface-raised)] text-[var(--ink)] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
            <form onSubmit={submit} className="p-4 sm:p-6 space-y-4" noValidate>
                {/* The shared phone dialog supplies its own close (x); the footer button closes on every width. */}
                <h2 className="text-xl font-bold pr-12">{title}</h2>

                <div>
                    <label htmlFor="event-name" className={label}>{words.event} name</label>
                    <input id="event-name" className={INPUT_CLASS} value={form.event_name} onChange={set('event_name')} disabled={readOnly} placeholder="e.g. Smith wedding reception" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="event-type" className={label}>Type of {words.eventLower}</label>
                        <select id="event-type" className={INPUT_CLASS} value={form.event_type} onChange={chooseType} disabled={readOnly}>
                            <option value="">Choose a type…</option>
                            {kits.filter((kit) => kit.key !== 'other').map((kit) => <option key={kit.key} value={kit.key}>{kit.label}</option>)}
                            <option value="other">Other</option>
                        </select>
                        {form.event_type === 'other' && (
                            <div className="mt-2">
                                <label htmlFor="event-type-other" className={label}>Type name</label>
                                <input id="event-type-other" className={INPUT_CLASS} maxLength={60} value={form.event_type_other || ''} onChange={set('event_type_other')} disabled={readOnly} placeholder="e.g. Trade show" />
                            </div>
                        )}
                        {selectedKit?.description && <p className="mt-1 text-xs text-[var(--ink-muted)]">{selectedKit.description}</p>}
                    </div>
                    <div>
                        <label htmlFor="event-guests" className={label}>Guests expected</label>
                        <input id="event-guests" type="number" min="0" inputMode="numeric" className={INPUT_CLASS} value={form.expected_guest_count} onChange={set('expected_guest_count')} disabled={readOnly} />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="event-date" className={label}>Start date</label>
                        <input id="event-date" type="date" className={INPUT_CLASS} value={form.event_date} onChange={set('event_date')} disabled={readOnly} />
                    </div>
                    <div>
                        <label htmlFor="event-start" className={label}>Start time</label>
                        <TimeCombo id="event-start" label="Start time" inputClassName={INPUT_CLASS} value={form.start_time} onChange={(start_time) => setForm((previous) => ({ ...previous, start_time }))} disabled={readOnly} />
                    </div>
                    <div>
                        <label htmlFor="event-end-date" className={label}>End date</label>
                        <input id="event-end-date" type="date" min={form.event_date || undefined} className={INPUT_CLASS} value={form.end_date || form.event_date} onChange={set('end_date')} disabled={readOnly} />
                    </div>
                    <div>
                        <label htmlFor="event-end" className={label}>End time</label>
                        <TimeCombo id="event-end" label="End time" inputClassName={INPUT_CLASS} value={form.end_time} onChange={(end_time) => setForm((previous) => ({ ...previous, end_time }))} disabled={readOnly} />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="event-venue" className={label}>Venue name</label>
                        <VendorPicker id="event-venue" kind="venue" label="Venue name" maxLength={200} list={picker} inputClassName={INPUT_CLASS} value={form.venue_name} vendorId={form.venue_vendor_id} address={form.venue_address} disabled={readOnly}
                            onChange={(v) => setForm((previous) => ({ ...previous, venue_name: v.name, venue_address: v.address, venue_vendor_id: v.vendor_id }))} />
                    </div>
                    <div>
                        <label htmlFor="event-address" className={label}>Venue address</label>
                        {readOnly
                            ? <input id="event-address" className={INPUT_CLASS} value={form.venue_address} disabled readOnly />
                            : <AddressField client={client} id="event-address" module="events" label="Venue address" inputClassName={INPUT_CLASS} value={form.venue_address} disabled={Boolean(form.venue_vendor_id)} onChange={(venue_address) => setForm((previous) => ({ ...previous, venue_address }))} />}
                    </div>
                </div>

                {needsEntityPick && !isEdit && (
                    <div>
                        <label htmlFor="event-entity" className={label}>Business</label>
                        <select id="event-entity" className={INPUT_CLASS} value={form.entity_id} onChange={set('entity_id')}>
                            <option value="">Choose…</option>
                            {entities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
                        </select>
                    </div>
                )}

                {(picker.ready || (form.hotels || []).length > 0) && !readOnly && (
                    <div data-testid="event-hotels">
                        <p className={label}>Hotels</p>
                        <HotelsField client={client} list={picker} hotels={form.hotels || []} inputClassName={INPUT_CLASS} onChange={(hotels) => setForm((previous) => ({ ...previous, hotels }))} />
                    </div>
                )}

                {showSettings && (
                    <fieldset ref={settingsRef} tabIndex={-1} id="event-guest-settings" className="space-y-2 outline-none" data-testid={isEdit ? 'edit-event-guest-options' : 'new-event-guest-options'}>
                        <legend className={label}>Invitation settings</legend>
                        <label className="ef-inline"><input type="checkbox" checked={Boolean(form.plus_ones_enabled)} onChange={(event) => setForm((previous) => ({ ...previous, plus_ones_enabled: event.target.checked, plus_ones_max: previous.plus_ones_max || 1 }))} data-testid="new-event-plus" />Guests may bring plus-ones</label>
                        {form.plus_ones_enabled && (
                            <div>
                                <label htmlFor="event-plus-max" className={label}>Most plus-ones per guest</label>
                                <input id="event-plus-max" type="number" min="1" max="10" inputMode="numeric" className={INPUT_CLASS} value={form.plus_ones_max} onChange={set('plus_ones_max')} />
                            </div>
                        )}
                        <p className="text-sm font-medium">How guests get transportation</p>
                        {TRANSPORT_MODES.map((mode) => (
                            <label key={mode.key} className="ef-inline"><input type="radio" name="event-transport-mode" checked={form.guest_transport_mode === mode.key} onChange={() => setForm((previous) => ({ ...previous, guest_transport_mode: mode.key }))} data-testid={`new-event-mode-${mode.key}`} /><span><strong>{mode.label}.</strong> {mode.help}</span></label>
                        ))}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div><label htmlFor="event-provider" className={label}>Transport provider</label>
                                <VendorPicker id="event-provider" kind="transport_provider" label="Transport provider" list={picker} inputClassName={INPUT_CLASS} value={form.transport_provider_name} vendorId={form.provider_vendor_id} onChange={(v) => setForm((previous) => ({ ...previous, transport_provider_name: v.name, provider_vendor_id: v.vendor_id }))} /></div>
                            <div><label htmlFor="event-coordinator" className={label}>Event coordinator</label><input id="event-coordinator" className={INPUT_CLASS} maxLength={120} value={form.coordinator_name} onChange={set('coordinator_name')} /></div>
                            <div><label htmlFor="event-coordinator-phone" className={label}>Coordinator phone</label><input id="event-coordinator-phone" type="tel" className={INPUT_CLASS} maxLength={30} value={form.coordinator_phone} onChange={set('coordinator_phone')} /></div>
                            <div><label htmlFor="event-coordinator-email" className={label}>Coordinator email</label><input id="event-coordinator-email" type="email" className={INPUT_CLASS} maxLength={254} value={form.coordinator_email} onChange={set('coordinator_email')} /></div>
                        </div>
                        <GuestVisibilityChecklist value={form.guest_visibility} plusOnesEnabled={form.plus_ones_enabled} transportMode={form.guest_transport_mode} onChange={(guest_visibility) => setForm((previous) => ({ ...previous, guest_visibility }))} />
                    </fieldset>
                )}

                <div>
                    <label htmlFor="event-notes" className={label}>Notes{selectedKit && !isEdit ? ' (starter checklist added — edit freely)' : ''}</label>
                    <textarea id="event-notes" rows={5} className={INPUT_CLASS} value={form.notes} onChange={set('notes')} disabled={readOnly} />
                </div>

                {shownError && <div role="alert" className="rounded-lg border border-[var(--ui-alert)] p-3 text-sm text-[var(--ink)]">{shownError}</div>}

                <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className={ACTION_PILL_QUIET_CLASS}>{readOnly ? 'Close' : 'Cancel'}</button>
                    {!readOnly && <button type="submit" disabled={saving} className={`${ACTION_PILL_CLASS} disabled:opacity-50`}>{saving ? 'Saving…' : 'Save'}</button>}
                </div>
            </form>
        </ModalSurface>
    );
}
